import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import toast from 'react-hot-toast'
import { likePost, unlikePost } from 'modules/community/api/community-api'
import { communityPostKeys } from 'modules/community/api/key-factory'
import type { PostInterface } from 'modules/community/types/post-interface'

export const useToggleLike = (): ((post: PostInterface) => () => Promise<void>) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const likeMutation = useMutation({
    mutationFn: async ({
      id,
      isLiked,
    }: {
      id: number
      isLiked: boolean
    }): Promise<{ id: number; isLiked: boolean }> => {
      await (isLiked ? unlikePost(id) : likePost(id))
      return { id, isLiked }
    },
    onMutate: async ({ id, isLiked }) => {
      await queryClient.cancelQueries({ queryKey: communityPostKeys.all })

      const snapshot = queryClient.getQueriesData({ queryKey: communityPostKeys.all })

      const toggleLike = (items: PostInterface[]): PostInterface[] =>
        items.map(post =>
          post.id === id
            ? {
                ...post,
                isLiked: !isLiked,
                likesCount: isLiked ? post.likesCount - 1 : post.likesCount + 1,
              }
            : post,
        )

      queryClient.setQueriesData<PostInterface[]>(
        { queryKey: communityPostKeys.pinnedLists() },
        old => (old ? toggleLike(old) : old),
      )
      queryClient.setQueriesData<{ pages: { items: PostInterface[] }[] }>(
        { queryKey: communityPostKeys.all },
        old => {
          if (!old || !('pages' in old)) return old
          return {
            ...old,
            pages: old.pages.map(page => ({ ...page, items: toggleLike(page.items) })),
          }
        },
      )
      queryClient.setQueriesData<PostInterface>(
        { queryKey: communityPostKeys.details() },
        oldPost => {
          if (oldPost?.id !== id) return oldPost
          return {
            ...oldPost,
            isLiked: !isLiked,
            likesCount: isLiked ? oldPost.likesCount - 1 : oldPost.likesCount + 1,
          }
        },
      )

      return { snapshot }
    },
    onError: (_err, _variables, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityPostKeys.all })
    },
  })
  const { mutateAsync: likeMutateAsync } = likeMutation

  return useCallback(
    (post: PostInterface) => async () => {
      try {
        await likeMutateAsync({ id: post.id, isLiked: post.isLiked })
      } catch {
        toast.error(t('post_details.error.error_like_post'))
      }
    },
    [t, likeMutateAsync],
  )
}
